import { AppEvents } from "../types";

type EventHandler<Args extends unknown[]> = (
  ...args: Args
) => void | Promise<void>;

declare const global: any;

export class EventManager<Events extends Record<string, unknown[]>> {
  private static instances = new Map<any, any>();

  private listeners: { [K in keyof Events]?: EventHandler<Events[K]>[] } = {};
  private deferredQueue: Array<{
    name: keyof Events;
    args: Events[keyof Events];
  }> = [];
  private eventQueue: Array<{
    name: keyof Events;
    args: Events[keyof Events];
  }> = [];
  private isReady = false;
  private isProcessing = false;

  private constructor() {}

  public static getInstance<
    T extends Record<string, unknown[]>
  >(): EventManager<T> {
    if (!EventManager.instances.has(global)) {
      EventManager.instances.set(global, new EventManager<T>());
    }
    return EventManager.instances.get(global) as EventManager<T>;
  }

  public initialize(): void {
    if (this.isReady) return;
    this.isReady = true;
    this.eventQueue.push(...this.deferredQueue);
    this.deferredQueue = [];
    this.processNext();
  }

  public on<K extends keyof Events>(
    eventName: K,
    handler: EventHandler<Events[K]>
  ): () => void {
    this.listeners[eventName] = this.listeners[eventName] || [];
    this.listeners[eventName]!.push(handler);
    return () => this.off(eventName, handler);
  }

  public off<K extends keyof Events>(
    eventName: K,
    handler: EventHandler<Events[K]>
  ): void {
    const handlers = this.listeners[eventName];
    if (!handlers) return;
    this.listeners[eventName] = handlers.filter((h) => h !== handler);
    if (this.listeners[eventName]!.length === 0) {
      delete this.listeners[eventName];
    }
  }

  public emit<K extends keyof Events>(eventName: K, ...args: Events[K]): void {
    const entry = { name: eventName, args };
    if (!this.isReady) {
      this.deferredQueue.push(entry);
    } else {
      this.eventQueue.push(entry);
    }
    this.processNext();
  }

  public once<K extends keyof Events>(
    eventName: K,
    handler: EventHandler<Events[K]>
  ): () => void {
    const wrapper: EventHandler<Events[K]> = async (...args) => {
      await handler(...args);
      this.off(eventName, wrapper);
    };
    return this.on(eventName, wrapper);
  }

  private async processNext(): Promise<void> {
    if (this.isProcessing) return;
    const next = this.eventQueue.shift();
    if (!next) return;

    this.isProcessing = true;
    const handlers = this.listeners[next.name] ?? [];

    for (const handler of handlers.slice()) {
      try {
        await handler(...(next.args as Events[typeof next.name]));
      } catch (err) {
        console.error(
          `[EventManager] error in handler for ${String(next.name)}:`,
          err
        );
      }
    }

    this.isProcessing = false;
    return this.processNext();
  }
}

export const eventManager = EventManager.getInstance<AppEvents>();
